import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
dayjs.extend(customParseFormat);
export interface Discount {
  ageStart: number;
  ageEnd: number;
  discountRate: number;
}
export interface Adjustment {
  employeeDiscount: Discount[];
  familyFriendsResidingDiscount: Discount[];
  familyFriendsNotResidingDiscount: number;
  companionDiscount: number;
}
export interface Allocation {
  allocationType: string;
  allocationCode: string;
  coverageType: string;
  percentage: number;
  _id: string;
}
export interface AllocationsDetail {
  code: string;
  planCode: string;
  benefitType: string;
  effectDate: string;
  effectiveDate: string;
  adjustments?: Adjustment[];
  allocations: Allocation[];
  createdAt?: string | null;
  updatedAt?: string | null;
  createdBy?: string | null;
  updatedBy?: string | null;
}
export interface AllocationsState {
  plans: Array<{ _id: string }>;
  riders: Array<{ _id: string }>;
  effectiveDates: Array<{ _id: string }>;
  selectedPlan: string | null;
  selectedRider: string | null;
  selectedEffectiveDate: string | null;
  newEffectiveDate: string | null;
  allocationsDetail: AllocationsDetail | null;
  editAllocations?: Allocation[] | null;
  error?: string | null;
  success?: string | null;
  dateError?: string | null;
}

export const initialState: AllocationsState = {
  plans: [],
  riders: [],
  effectiveDates: [],
  selectedPlan: null,
  selectedRider: null,
  selectedEffectiveDate: null,
  newEffectiveDate: null,
  allocationsDetail: null,
  editAllocations: null,
  error: null,
  success: null,
  dateError: null
};

const ERROR_DELETE_ALLOCATION = 'Failed to delete allocation';

export const fetchAllocationsPlans = createAsyncThunk('allocations/fetchPlans', async () => {
  const response = await axios.get('/api/product/allocationsdropdown');
  return response.data;
});

export const fetchRiders = createAsyncThunk(
  'allocations/fetchRiders',
  async ({ code }: { code: string }) => {
    const response = await axios.get(`/api/product/allocationsdropdown?code=${code}`);
    return response.data;
  }
);

export const fetchAllocationsEffectiveDates = createAsyncThunk(
  'allocations/fetchEffectiveDates',
  async ({ code, planCode }: { code: string; planCode: string }) => {
    const response = await axios.get(`/api/product/allocationsdropdown?code=${planCode}&planCode=${code}`);
    return response.data;
  }
);

export const fetchAllocationsDetail = createAsyncThunk(
  'allocations/fetchDetail',
  async ({ code, planCode, effectDate }: { code: string; planCode: string; effectDate: string }) => {
    const response = await axios.get(
      `/api/product/allocations?code=${code}&planCode=${planCode}&effectDate=${effectDate}`
    );

    return response.data;
  }
);

export const saveAllocationsDetail = createAsyncThunk<
  any,
  void,
  { state: { allocations: AllocationsState } }
>(
  'allocations/saveDetail',
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState() as { allocations: AllocationsState };
      const { allocationsDetail, editAllocations } = state.allocations;
      if (!allocationsDetail || !editAllocations) {
        return rejectWithValue('No allocation data to save.');
      }
      const payload = {
        ...allocationsDetail,
        allocations: editAllocations
      };
      const response = await axios.post('api/product/allocations/update', payload);
      return response.data;
    } catch (err) {
      const data = err?.response?.data;
      const apiMessage = data?.error || (Array.isArray(data?.errors) && data.errors[0]?.message) || data?.message || null;
      const message = apiMessage || err?.response?.data || err?.message || 'Failed to save allocations';
      return rejectWithValue(message);
    }
  }
);

export const deleteAllocations = createAsyncThunk(
  'allocations/deleteAllocations',
  async(
    { code, planCode, effectDate }: { code: string; planCode: string; effectDate: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await axios.post(`/api/product/allocations/delete?code=${planCode}&planCode=${code}&effectDate=${effectDate}`);
      return response.data;
    } catch (err) {
      const data = err?.response?.data;
      const apiMessage = data?.error || (Array.isArray(data?.errors) && data.errors[0]?.message) || null;
  const message = apiMessage || err?.response?.data || err?.message || ERROR_DELETE_ALLOCATION;
      return rejectWithValue(message);
    }
  }
)

export const allocationsSlice = createSlice({
  name: 'allocations',
  initialState,
  reducers: {
    setAllocationsSelectedPlan: (state, action) => {
      state.selectedPlan = action.payload;
    },
    setAllocationsSelectedRider: (state, action) => {
      state.selectedRider = action.payload;
    },
    setAllocationsSelectedEffectiveDate: (state, action) => {
      state.selectedEffectiveDate = action.payload;
    },
    resetAllocationsDetail: (state) => {
      state.allocationsDetail = null;
      state.editAllocations = null;
      state.newEffectiveDate = null;
      state.selectedPlan = null;
      state.selectedRider = null;
      state.selectedEffectiveDate = null;
      state.effectiveDates = [];
      state.plans = [];
      state.riders = [];
      state.dateError = null;
    },
    clearAllocationsMessages: (state) => {
      state.success = null;
      state.error = null;
    },
    initializeEditAllocations: (state) => {
      state.editAllocations = state.allocationsDetail?.allocations
        ? JSON.parse(JSON.stringify(state.allocationsDetail.allocations))
        : null;
    },
    setEditAllocations: (state, action) => {
      state.editAllocations = action.payload || null;
    },
    setAllocationsNewEffectiveDate: (state, action) => {
      const payload = action.payload || {} as { formattedDate?: string; isoDate?: string };
      const formattedDate = payload.formattedDate ?? '';
      const isoDate = payload.isoDate ?? '';
      state.newEffectiveDate = formattedDate;
      state.dateError = '';
      if (state.allocationsDetail) {
        state.allocationsDetail.effectDate = formattedDate;
        state.allocationsDetail.effectiveDate = isoDate || formattedDate;
      }
    },
    clearDateError: (state) => {
      state.dateError = '';
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchAllocationsPlans.fulfilled, (state, action) => {
        state.plans = action.payload;
      })
      .addCase(fetchAllocationsPlans.rejected, (state, action) => {
        state.plans = [];
        state.error = action.error.message || 'Failed to fetch plans';
      })
      .addCase(fetchRiders.fulfilled, (state, action) => {
        state.riders = action.payload;
      })
      .addCase(fetchRiders.rejected, (state, action) => {
        state.riders = [];
        state.error = action.error.message || 'Failed to fetch riders';
      })
      .addCase(fetchAllocationsEffectiveDates.fulfilled, (state, action) => {
        state.effectiveDates = action.payload;
      })
      .addCase(fetchAllocationsEffectiveDates.rejected, (state, action) => {
        state.effectiveDates = [];
        state.error = action.error.message || 'Failed to fetch effective dates';
      })
      .addCase(fetchAllocationsDetail.fulfilled, (state, action) => {
        state.allocationsDetail = action.payload;
        state.editAllocations = JSON.parse(JSON.stringify(action.payload.allocations || []));
      })
      .addCase(fetchAllocationsDetail.rejected, (state, action) => {
        state.allocationsDetail = null;
        state.error = action.error.message || 'Failed to fetch allocation details';
      })
      .addCase(saveAllocationsDetail.fulfilled, (state, action) => {
        state.success = action.payload?.message || 'Allocations saved successfully';
        state.error = null;
        if (action.payload) {
          state.allocationsDetail = action.payload;
          state.editAllocations = JSON.parse(JSON.stringify(action.payload.allocations || []));
        }
      })
      .addCase(saveAllocationsDetail.rejected, (state, action) => {
        state.error = action.payload as string;
        state.success = null;
      })
      .addCase(deleteAllocations.fulfilled, (state, action) => {
        if (!action.payload || !action.payload.deleted) {
          state.success = null;
          state.error = ERROR_DELETE_ALLOCATION;
          return;
        } else {
          state.success = 'Allocation deleted successfully';
          state.error = null;
        }
      })
      .addCase(deleteAllocations.rejected, (state, action) => {
        const payloadMessage = action.payload as string | undefined;
        const fallback = action.error?.message || ERROR_DELETE_ALLOCATION;
        state.error = payloadMessage || fallback;
        state.success = null;
      });
  }
});

export const {
  setAllocationsSelectedPlan,
  setAllocationsSelectedRider,
  setAllocationsSelectedEffectiveDate,
  setAllocationsNewEffectiveDate,
  resetAllocationsDetail,
  initializeEditAllocations,
  setEditAllocations,
  clearAllocationsMessages,
} = allocationsSlice.actions;

export default allocationsSlice.reducer;
